import React, { useEffect, useState } from "react";
import {
  Dialog,
  Slide,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { BRAND } from "../../components/auth/authTheme";
import ProtectedImage from "../../components/ProtectedImage";
import { getUserInfoFromToken } from "../../utils/authUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MOBILE_TABS = [
  { id: "profile", label: "Profile" },
  { id: "work", label: "Work & Pay" },
  { id: "leave", label: "Leave & Access" },
];

const PermissionBadge = ({ checked, label }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      bgcolor: checked ? BRAND.orangeLight : BRAND.bg,
      color: checked ? BRAND.orange : BRAND.muted,
      fontWeight: 600,
      fontSize: "0.7rem",
      border: `1px solid ${checked ? BRAND.orange : BRAND.border}`,
      textTransform: "capitalize",
    }}
  />
);

const PERMISSION_KEYS = [
  "view", "edit", "create", "delete", "approve", "reject",
  "assign", "unassign", "export", "import", "generate_report",
  "archive", "restore",
];

const EMPLOYMENT_TYPE_LABELS = {
  FT: "Full Time",
  PT: "Part Time",
  CT: "Contract",
  IN: "Intern",
};

const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatAttendanceType = (str) => {
  if (!str) return null;
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const isEmployerUser = () =>
  (getUserInfoFromToken().userType || "").toLowerCase() === "employer";

const DetailRow = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      py: 1.35,
      borderBottom: `1px solid ${BRAND.border}`,
      "&:last-child": { borderBottom: "none", pb: 0 },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        bgcolor: "var(--brand-orange-light, rgba(230,90,0,0.12))",
        color: "var(--brand-orange, #E65A00)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.6875rem",
          color: BRAND.muted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.9375rem",
          color: value ? BRAND.navy : BRAND.muted,
          fontWeight: value ? 500 : 400,
          wordBreak: "break-word",
          mt: 0.25,
          lineHeight: 1.35,
        }}
      >
        {value || "Not provided"}
      </Typography>
    </Box>
  </Box>
);

const DetailSection = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 700,
        color: BRAND.muted,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        mb: 1,
        px: 0.25,
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "16px",
        p: 2,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 2px 12px rgba(26, 43, 74, 0.05)",
      }}
    >
      {children}
    </Box>
  </Box>
);

const SegmentedTabs = ({ tabs, activeIndex, onChange }) => (
  <Box
    sx={{
      display: "flex",
      gap: 0.75,
      p: 0.75,
      bgcolor: "#fff",
      borderBottom: `1px solid ${BRAND.border}`,
    }}
  >
    {tabs.map((tab, index) => {
      const isActive = index === activeIndex;
      return (
        <Box
          key={tab.id}
          component="button"
          type="button"
          onClick={() => onChange(index)}
          sx={{
            flex: 1,
            border: "none",
            cursor: "pointer",
            borderRadius: "10px",
            py: 1.1,
            px: 0.5,
            minHeight: 44,
            bgcolor: isActive
              ? "var(--brand-orange, #E65A00)"
              : BRAND.bg,
            color: isActive ? "#fff" : BRAND.muted,
            fontWeight: 700,
            fontSize: "0.75rem",
            fontFamily: "inherit",
            transition: "background-color 0.15s, color 0.15s",
            lineHeight: 1.2,
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {tab.label}
        </Box>
      );
    })}
  </Box>
);

const EmployeeMobileDetailView = ({ show, handleClose, selectedEmployee }) => {
  const [activeTab, setActiveTab] = useState(0);
  const hideCompany = isEmployerUser();

  useEffect(() => {
    if (!show) setActiveTab(0);
  }, [show]);

  if (!selectedEmployee) return null;

  const profile = selectedEmployee.profile_data || {};
  const salary = selectedEmployee.employee_salary || {};
  const leave = selectedEmployee.leave_balance || {};
  const fullName =
    `${selectedEmployee.first_name || ""} ${selectedEmployee.last_name || ""}`.trim();
  const initials =
    `${selectedEmployee.first_name?.[0] || ""}${selectedEmployee.last_name?.[0] || ""}`.toUpperCase();
  const reportingManager = profile.reporting_manager
    ? `${profile.reporting_manager.first_name || ""} ${profile.reporting_manager.last_name || ""}`.trim()
    : null;
  const leaveManagers = leave.leave_approval_managers?.length
    ? leave.leave_approval_managers
        .map((m) => `${m.first_name || ""} ${m.last_name || ""}`.trim())
        .filter(Boolean)
        .join(", ")
    : null;
  const deductions = salary.deductions
    ? Object.entries(salary.deductions)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ")
    : null;
  const challans = Array.isArray(salary.challans) ? salary.challans : [];
  const permissions = selectedEmployee.permissions?.permissions || [];

  return (
    <Dialog
      fullScreen
      open={show}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          bgcolor: BRAND.bg,
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          borderRadius: 0,
          m: 0,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderBottom: `1px solid ${BRAND.border}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 1,
            px: 0.5,
            pr: 2,
          }}
        >
          <IconButton
            sx={{ color: BRAND.navy }}
            onClick={handleClose}
            aria-label="Close employee details"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "1.0625rem",
                fontWeight: 700,
                color: BRAND.navy,
                lineHeight: 1.25,
              }}
            >
              Employee Details
            </Typography>
            {selectedEmployee.employee_id && (
              <Typography
                sx={{ fontSize: "0.75rem", color: BRAND.muted, fontWeight: 500 }}
              >
                {selectedEmployee.employee_id}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 3,
        }}
      >
        <Box
          sx={{
            bgcolor: "#fff",
            pt: 3,
            pb: 2,
            px: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderBottom: `1px solid ${BRAND.border}`,
          }}
        >
          {profile.profile_image ? (
            <Box
              sx={{
                width: 84,
                height: 84,
                borderRadius: "50%",
                overflow: "hidden",
                mb: 1.5,
                border: "3px solid var(--brand-orange-light, rgba(230,90,0,0.15))",
              }}
            >
              <ProtectedImage
                path={profile.profile_image}
                alt={fullName}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          ) : (
            <Avatar
              sx={{
                width: 84,
                height: 84,
                mb: 1.5,
                bgcolor: "var(--brand-orange, #E65A00)",
                fontSize: "1.75rem",
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>
          )}
          <Typography
            sx={{
              fontSize: "1.1875rem",
              fontWeight: 700,
              color: BRAND.navy,
              textAlign: "center",
            }}
          >
            {fullName}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: BRAND.muted,
              mb: 1.25,
              textAlign: "center",
            }}
          >
            {profile.designation_name || "Designation not set"}
          </Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
            <Chip
              label={selectedEmployee.is_active ? "Active" : "Inactive"}
              size="small"
              sx={{
                bgcolor: selectedEmployee.is_active
                  ? "rgba(22, 163, 74, 0.12)"
                  : "rgba(107, 114, 128, 0.12)",
                color: selectedEmployee.is_active ? BRAND.success : BRAND.muted,
                fontWeight: 600,
              }}
            />
            {!hideCompany && selectedEmployee.company?.company_name && (
              <Chip
                label={selectedEmployee.company.company_name}
                size="small"
                sx={{
                  bgcolor: "rgba(27, 132, 255, 0.1)",
                  color: "#1b84ff",
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </Box>

        <SegmentedTabs
          tabs={MOBILE_TABS}
          activeIndex={activeTab}
          onChange={setActiveTab}
        />

        <Box sx={{ px: 2, pt: 2 }}>
          {activeTab === 0 && (
            <>
              <DetailSection title="Contact">
                <DetailRow
                  icon={<EmailOutlinedIcon fontSize="small" />}
                  label="Email"
                  value={selectedEmployee.email}
                />
                <DetailRow
                  icon={<PhoneOutlinedIcon fontSize="small" />}
                  label="Mobile"
                  value={selectedEmployee.mobile_number}
                />
                <DetailRow
                  icon={<PersonOutlineIcon fontSize="small" />}
                  label="Gender"
                  value={capitalizeFirstLetter(selectedEmployee.gender)}
                />
                <DetailRow
                  icon={<PublicOutlinedIcon fontSize="small" />}
                  label="Nationality"
                  value={selectedEmployee.nationality}
                />
              </DetailSection>

              <DetailSection title="Job Summary">
                {!hideCompany && (
                  <DetailRow
                    icon={<BusinessOutlinedIcon fontSize="small" />}
                    label="Company"
                    value={selectedEmployee.company?.company_name}
                  />
                )}
                <DetailRow
                  icon={<WorkOutlineIcon fontSize="small" />}
                  label="Department"
                  value={profile.department_name}
                />
                <DetailRow
                  icon={<WorkOutlineIcon fontSize="small" />}
                  label="Designation"
                  value={profile.designation_name}
                />
                <DetailRow
                  icon={<CalendarTodayOutlinedIcon fontSize="small" />}
                  label="Joining Date"
                  value={profile.joining_date}
                />
                <DetailRow
                  icon={<WorkOutlineIcon fontSize="small" />}
                  label="Employment Type"
                  value={
                    EMPLOYMENT_TYPE_LABELS[profile.employment_type] ||
                    profile.employment_type
                  }
                />
                <DetailRow
                  icon={<LocationOnOutlinedIcon fontSize="small" />}
                  label="Work Location"
                  value={profile.work_location}
                />
                <DetailRow
                  icon={<SupervisorAccountOutlinedIcon fontSize="small" />}
                  label="Reporting Manager"
                  value={reportingManager}
                />
                <DetailRow
                  icon={<PersonOutlineIcon fontSize="small" />}
                  label="Status"
                  value={profile.employee_status}
                />
              </DetailSection>
            </>
          )}

          {activeTab === 1 && (
            <>
              <DetailSection title="Schedule">
                <DetailRow
                  icon={<AccessTimeOutlinedIcon fontSize="small" />}
                  label="Shift Timing"
                  value={profile.shift_timing}
                />
                <DetailRow
                  icon={<EventBusyOutlinedIcon fontSize="small" />}
                  label="Weekly Off"
                  value={profile.weekly_off}
                />
                <DetailRow
                  icon={<AccessTimeOutlinedIcon fontSize="small" />}
                  label="Working Hours"
                  value={displayValue(profile.working_hours)}
                />
                <DetailRow
                  icon={<DescriptionOutlinedIcon fontSize="small" />}
                  label="Job Description"
                  value={profile.job_description}
                />
              </DetailSection>

              <DetailSection title="Salary">
                <DetailRow
                  icon={<AttachMoneyOutlinedIcon fontSize="small" />}
                  label="Basic Salary"
                  value={displayValue(salary.basic_salary)}
                />
                <DetailRow
                  icon={<AttachMoneyOutlinedIcon fontSize="small" />}
                  label="HRA"
                  value={displayValue(salary.hra)}
                />
                <DetailRow
                  icon={<AttachMoneyOutlinedIcon fontSize="small" />}
                  label="Bonus"
                  value={displayValue(salary.bonus)}
                />
                <DetailRow
                  icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                  label="Deductions"
                  value={deductions}
                />
              </DetailSection>

              <DetailSection title="Bank & Tax">
                <DetailRow
                  icon={<AccountBalanceOutlinedIcon fontSize="small" />}
                  label="Bank Name"
                  value={salary.bank_name}
                />
                <DetailRow
                  icon={<AccountBalanceOutlinedIcon fontSize="small" />}
                  label="Account Number"
                  value={salary.account_number}
                />
                <DetailRow
                  icon={<AccountBalanceOutlinedIcon fontSize="small" />}
                  label="IFSC Code"
                  value={salary.ifsc_code}
                />
                <DetailRow
                  icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                  label="PAN / Tax ID"
                  value={salary.pan_number}
                />
                <DetailRow
                  icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                  label="UAN / PF Number"
                  value={salary.pf_number}
                />
                <DetailRow
                  icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                  label="ESI Number"
                  value={salary.esi_number}
                />
              </DetailSection>

              <DetailSection title="Challans">
                {challans.length === 0 ? (
                  <Typography
                    sx={{ color: BRAND.muted, textAlign: "center", py: 2, fontSize: "0.875rem" }}
                  >
                    No challans assigned
                  </Typography>
                ) : (
                  <Box sx={{ mx: -1, overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: BRAND.bg }}>
                          {["#", "Name", "Amount", "%"].map((head) => (
                            <TableCell
                              key={head}
                              sx={{
                                fontWeight: 700,
                                color: BRAND.navy,
                                fontSize: "0.75rem",
                                borderColor: BRAND.border,
                                py: 1,
                              }}
                            >
                              {head}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {challans.map((challan, index) => (
                          <TableRow key={challan.id || index}>
                            <TableCell sx={{ borderColor: BRAND.border, fontSize: "0.8125rem" }}>
                              {index + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                borderColor: BRAND.border,
                                fontWeight: 600,
                                color: "var(--brand-orange, #E65A00)",
                                fontSize: "0.8125rem",
                              }}
                            >
                              {challan.name}
                            </TableCell>
                            <TableCell sx={{ borderColor: BRAND.border, fontSize: "0.8125rem" }}>
                              {challan.amount != null ? challan.amount : "—"}
                            </TableCell>
                            <TableCell sx={{ borderColor: BRAND.border, fontSize: "0.8125rem" }}>
                              {challan.percentage != null ? `${challan.percentage}%` : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </DetailSection>
            </>
          )}

          {activeTab === 2 && (
            <>
              <DetailSection title="Leave Settings">
                <DetailRow
                  icon={<SupervisorAccountOutlinedIcon fontSize="small" />}
                  label="Approval Managers"
                  value={leaveManagers}
                />
                <DetailRow
                  icon={<AccessTimeOutlinedIcon fontSize="small" />}
                  label="Attendance Type"
                  value={formatAttendanceType(leave.attendance_type)}
                />
                <DetailRow
                  icon={<CalendarTodayOutlinedIcon fontSize="small" />}
                  label="Leave Year"
                  value={displayValue(leave.leave_year)}
                />
                <DetailRow
                  icon={<BeachAccessOutlinedIcon fontSize="small" />}
                  label="Overtime Eligible"
                  value={
                    leave.overtime_eligible === true
                      ? "Yes"
                      : leave.overtime_eligible === false
                        ? "No"
                        : null
                  }
                />
                <DetailRow
                  icon={<BeachAccessOutlinedIcon fontSize="small" />}
                  label="Paid Leave Balance"
                  value={displayValue(leave.paid_leave_balance)}
                />
                <DetailRow
                  icon={<BeachAccessOutlinedIcon fontSize="small" />}
                  label="Unpaid Leave Balance"
                  value={displayValue(leave.unpaid_leave_balance)}
                />
              </DetailSection>

              <DetailSection title="App Permissions">
                {permissions.length === 0 ? (
                  <Typography
                    sx={{ color: BRAND.muted, textAlign: "center", py: 2, fontSize: "0.875rem" }}
                  >
                    No permissions configured
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {permissions.map((perm) => (
                      <Box
                        key={perm.module}
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          border: `1px solid ${BRAND.border}`,
                          bgcolor: perm.enabled ? BRAND.bg : "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "0.875rem" }}
                          >
                            {perm.moduleLabel || perm.module}
                          </Typography>
                          <Chip
                            label={perm.enabled ? "On" : "Off"}
                            size="small"
                            sx={{
                              bgcolor: perm.enabled ? BRAND.orangeLight : BRAND.bg,
                              color: perm.enabled ? BRAND.orange : BRAND.muted,
                              fontWeight: 600,
                              height: 24,
                            }}
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {PERMISSION_KEYS.map(
                            (key) =>
                              perm[key] !== undefined && (
                                <PermissionBadge
                                  key={key}
                                  checked={Boolean(perm[key])}
                                  label={key.replace(/_/g, " ")}
                                />
                              )
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </DetailSection>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default EmployeeMobileDetailView;
